<template>
  <div>

    <v-progress-linear v-if="loadingForms" indeterminate color="#107569" height="3" />


    <Treeselect v-model="selectedFormIds" :multiple="true" :options="formattedForms" :disable-branch-nodes="true"
      placeholder="Select one form per platform" :searchable="true" :clearable="true" :normalizer="normalizer"
      class="leads-select" @open="handleOpen" @input="handleSelection"
      :disabled="!(store.seleidn && Object.values(store.seleidn).some(v => v != null && v !== ''))" />
  </div>
</template>

<script>
import Treeselect from "vue3-treeselect";
import "vue3-treeselect/dist/vue3-treeselect.css";
import apiClient from "@/core/buildRequest";
import { useSuperAddStore } from "@/store/superAddStore";

export default {
  components: { Treeselect },
  data() {
    return {
      formattedForms: [],
      store: useSuperAddStore(),
      facebookAccountsRaw: localStorage.getItem("selected_identity_accounts"),
      loadingForms: false,
      selectedFormIds: [],
    };
  },
  watch: {
    selectedFormIds(newVal) {
      const platformSelected = {};
      const filtered = [];

      newVal
        .slice()
        .reverse()
        .forEach((formId) => {
          const platform = this.getPlatformById(formId);
          if (platform && !platformSelected[platform]) {
            platformSelected[platform] = true;
            filtered.unshift(formId);
          }
        });

      if (JSON.stringify(this.selectedFormIds) !== JSON.stringify(filtered)) {
        this.selectedFormIds = filtered;
      }

      const finalSelected = filtered.map((formId) => ({
        id: formId,
        platform: this.getPlatformById(formId),
      }));

      this.store.selectedForms = finalSelected;
    },
  },
  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.label,
        isDisabled: node.disabled || false,
      };
    },

    getPlatformById(formId) {
      for (const group of this.formattedForms) {
        if (group.children?.some((c) => c.id === formId)) return group.id;
      }
      return null;
    },

    handleSelection(newVal) {
      this.selectedFormIds = newVal;
    },

    async handleOpen() {
      if (this.loadingForms) return;
      this.loadingForms = true;
      this.formattedForms = [];

      try {
        const platformsRaw = localStorage.getItem("super_selected_platforms");
        const otherAccountsRaw = localStorage.getItem("superselected_accounts"); // المصدر الأساسي لكل المنصات
        if (!platformsRaw) return;

        const selectedPlatforms = (JSON.parse(platformsRaw) || []).map(p => {
          const name = p?.name?.toLowerCase();
          return name === "meta" ? "facebook" : name;
        });

        const ids = this.store.seleidn || {};
        const otherAccounts = JSON.parse(otherAccountsRaw || "{}");

        const pickId = v => (v && typeof v === "object" ? v.id : v) || null;
        const isNumericId = v => typeof v === "string" && /^\d+$/.test(v);

        const params = {};
        const platformsList = [];

        // Facebook: خده من seleidn أو superselected_accounts
        const fbId = pickId(ids.facebook) || pickId(otherAccounts.facebook);
        if (selectedPlatforms.includes("facebook") && fbId) {
          params.facebook_page_id = fbId;
          platformsList.push("facebook");
        }

        // Snapchat: حصريًا من superselected_accounts.snapchat.id (طلبك)
        const scId = pickId(otherAccounts?.snapchat); // <- ده بس
        if (selectedPlatforms.includes("snapchat") && scId) {
          params.snapchat_ad_account_id = scId;
          platformsList.push("snapchat");
        } else if (selectedPlatforms.includes("snapchat")) {
          console.warn("No valid Snapchat ad account id in superselected_accounts; skipping Snapchat.");
        }

        // TikTok: لازم ID رقمي. فضّل superselected_accounts على seleidn
        const ttFromOther =
          otherAccounts?.tiktok?.advertiser_id ||
          otherAccounts?.tiktok?.account_id ||
          pickId(otherAccounts?.tiktok);
        let ttId = null;
        if (isNumericId(ttFromOther)) ttId = ttFromOther;
        else if (isNumericId(ids.tiktok)) ttId = ids.tiktok;

        if (selectedPlatforms.includes("tiktok") && ttId) {
          params.tiktok_ad_account_id = ttId;
          platformsList.push("tiktok");
        } else if (selectedPlatforms.includes("tiktok")) {
          console.warn("TikTok ad account id missing/invalid; skipping TikTok.");
        }

        if (platformsList.length === 0) return;

        platformsList.forEach((p, i) => (params[`platforms[${i}]`] = p));
        params.time = Date.now();
        const response = await apiClient.get("lead-center/get/lead-forms", { params });
        const data = response.data?.data?.success || [];

        const platformsMap = {
          facebook: { label: "Meta", children: [] },
          snapchat: { label: "Snapchat", children: [] },
          tiktok: { label: "TikTok", children: [] },
        };

        data.forEach((form) => {
          const platform = form.platform?.toLowerCase();
          if (platformsMap[platform]) {
            platformsMap[platform].children.push({ id: form.id, label: form.name });
          }
        });

        this.formattedForms = Object.entries(platformsMap)
          .filter(([k]) => selectedPlatforms.includes(k))
          .map(([k, g]) => ({ id: k, label: g.label, children: g.children }));
      } catch (e) {
        console.error("❌ Failed to load lead forms:", e);
      } finally {
        this.loadingForms = false;
      }
    },

    validate() {
      const errors = [];

      if (!this.formattedForms.length) {
        errors.push("Please select lead.");
        return errors;
      }

      const allSelected = this.formattedForms.every((platformGroup) =>
        platformGroup.children.some((form) =>
          this.selectedFormIds.includes(form.id)
        )
      );

      if (!allSelected) {
        errors.push("Please select at least one lead form for all platforms.");
      }

      return errors;
    },

    getData() {
      return {
        selectedForms: this.selectedFormIds,
      };
    },
  },
  computed: {
    hasAnyIdentity() {
      const s = this.store.seleidn;
      if (!s || typeof s !== 'object') return false;
      return Object.values(s).some(v => v != null && (v === true || v === false || v?.id || v));
    },
  }

};
</script>

<style scoped>
.leads-select {
  min-height: 3rem;
}

::v-deep(.vue-treeselect__control) {
  min-height: 3rem;
}
</style>
